/**
 * State for the dynamic shipping selection modal on Create RMA.
 * Owns the full lifecycle: fetches available options from the API, controls modal visibility,
 * and exposes a promise-based interface so callers can await the user's choice before proceeding.
 */
import { makeAutoObservable, runInAction } from "mobx";
import CreateRmaService, { IPortalDynamicShippingOptionDto } from "../../../services/api/create-rma-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import { ICreateRmaDto } from "./rma-request-store";

export default class CreateRmaDynamicShippingOptionsStore {
	private _shippingOptions: IPortalDynamicShippingOptionDto[] = [];
	private _showModal = false;
	private _selectedOptionId: number | null = null;
	private _isFetching: boolean = false;
	private _pendingSelection?: {
		promise: Promise<boolean>;
		resolve: (value: boolean) => void;
	};

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	public get shippingOptions(): IPortalDynamicShippingOptionDto[] {
		return this._shippingOptions;
	}

	public get showModal(): boolean {
		return this._showModal;
	}

	public get selectedOptionId(): number | null {
		return this._selectedOptionId;
	}

	public get hasSelection(): boolean {
		return this._selectedOptionId !== null;
	}

	public get isFetching(): boolean {
		return this._isFetching;
	}

	private get _createRmaService(): CreateRmaService { return rootContainer.get<CreateRmaService>(TYPE.CreateRmaService); }

	/**
	 * Fetches shipping options for the given request, then:
	 * - resolves immediately as 'true' when no dynamic shipping is required
	 * - opens the modal and waits for user confirmation or cancellation otherwise
	 * Throws on API error so callers can surface the failure to the user.
	 */
	public async waitForSelection(request: ICreateRmaDto): Promise<boolean> {
		this._reset();

		let resolve!: (value: boolean) => void;
		const promise = new Promise<boolean>((res) => {
			resolve = res;
		});
		this._pendingSelection = { promise, resolve };

		this._isFetching = true;

		try {
			const response = await this._createRmaService.getDynamicShippingOptions(request);
			/** Dynamic shipping not required for this request — proceed without showing the modal. */
			if (!response.is_dynamic_shipping_options_supported) {
				this._resolvePending(true);
				return promise;
			}

			runInAction(() => {
				this._shippingOptions = response.shipping_options;
				this._selectedOptionId = null;
				this._showModal = true;
			});
		} catch (e) {
			this._resolvePending(false);
		} finally {
			this._isFetching = false;
		}

		return promise;
	}

	private _resolvePending(value: boolean): void {
		const pending = this._pendingSelection;
		if (!pending) {
			return;
		}
		this._pendingSelection = undefined;
		pending.resolve(value);
	}

	private _ensurePendingSelection(): void {
		if (!this._pendingSelection) {
			throw new Error('No pending shipping option selection, should never happen');
		}
	}

	public setSelectedOptionId(id: number): void {
		this._selectedOptionId = id;
	}

	/** Resolves the pending selection with the chosen option and closes the modal. */
	public confirmSelection(): void {
		this._ensurePendingSelection();
		this._resolvePending(true);
		runInAction(() => {
			this._showModal = false;
		});
	}

	/** Resolves the pending selection as cancelled and resets state. */
	public cancelModal(): void {
		this._ensurePendingSelection();
		this._resolvePending(false);
		this._reset();
	}

	private _reset(): void {
		runInAction(() => {
			this._shippingOptions = [];
			this._showModal = false;
			this._selectedOptionId = null;
			this._pendingSelection = undefined;
		});
	}
}
